import React, {createContext, useEffect, useState, useRef} from 'react';
import formsConfig from '../../../config/forms.config';
import {useLocation} from 'react-router-dom'
import Toast from "@Components/Toast";
import Logger from '@ffw/logger-lib';

export const FormContext = createContext({});

export const FormProvider = ({children, context, authData, messages}) => {
  const location = useLocation();
  let initialFormData = {};
  let initialIsAuthenticated = false;
  if (context && context.data && Object.keys(context.data).length) {
    initialFormData = context.data;
  }
  else if (typeof window !== "undefined" && window.__ROUTE_DATA__) {
    initialFormData = window.__ROUTE_DATA__;
  }
  if (authData && authData.isAuthenticated) {
    initialIsAuthenticated = authData.isAuthenticated
  }
  else if (typeof window !== "undefined" && window.__AUTH_DATA__) {
    initialIsAuthenticated = window.__AUTH_DATA__.isAuthenticated;
  }

  const [formData, setFormData] = useState([]);
  const [errors, setErrors] = useState();
  const [formName, setFormName] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [additionalData, setAdditionalData] = useState(initialFormData);
  const [toast, setToast] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastLinks, setToastLinks] = useState([]);
  const [isAuthenticated] = useState(initialIsAuthenticated);
  const [timesRendered, setTimesRendered] = useState(0);
  const [editedItemKey, setEditedItemKey] = useState('');
  const [translations, setTranslations] = useState(messages);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const initializeSchema = (statusSettings = {}) => {
    const cutoverStatus = {
      register: statusSettings.myrandstad_register?.status,
      login: statusSettings?.myrandstad_login?.status,
    };
    const schema = formsConfig();
    ['register', 'login'].forEach(formKey => {
      if (schema[formKey] && Array.isArray(schema[formKey])) {
        const status = cutoverStatus[formKey];
        if (status !== undefined) {
          schema[formKey] = schema[formKey].map(section => ({
            ...section,
            data: section.data.map(item => ({
              ...item,
              components: item.components.map(component => ({
                ...component,
                settings: {
                  ...component.settings,
                  disabled: status,
                },
              })),
            })),
          }));
        }
      }
    });
    return schema;
  };
  const initSchema = initializeSchema(additionalData?.cutoverSettings);
  const [schema, setSchema] = useState(initSchema);

  useEffect(
    () => {
      setErrors();
    },
    [location]
  );

  const getFormSchema = name => {
    return schema[name];
  };

  const setFormSchema = (name, data) => {
    setSchema(prev => {
      return {
        ...prev,
        [name]: data
      }
    })
  };

  // Default change handler.
  const handleChange = event => {
    let name = event.target.name;
    let value = event.target.value;
    const skipField = event.target.getAttribute('data-skip-empty-field');
    if (event.target.type === 'checkbox') {
      let emptyValue = event.target.getAttribute('data-empty-field-value');
      let checkboxValue = event.target.value;
      if (typeof checkboxValue === 'string' && checkboxValue === 'true') {
        checkboxValue = true;
      }
      value = event.target.checked ? checkboxValue :
        emptyValue ? emptyValue : null;
    }

    setFormData((prev) => {
      if (!prev[currentStep]) {
        prev[currentStep] = {};
      }
      prev[currentStep][name] = value;

      // In some cases we do not want empty values, i.e. some checkboxes.
      if (!value && skipField) {
        delete prev[currentStep][name];
      }
      return {
        ...prev
      }
    });

    setErrors((prev) => {
      if (prev && prev.hasOwnProperty(name) && value && !skipField) {
        delete prev[name];
      }

      return {
        ...prev,
      };
    });
  };

  const handleErrors = response => {
    setErrors();
    const data = {};

    if (response.errors) {
      response.errors.map(error => {
        if (error.errorType && error.errorType === 'notice') {
          return data[error.path[0]] = {
            message: error.message,
            errorType: error.errorType
          }
        }
        return data[error.path[0]] = error.message;
      });

      if (response.error) {
        data.generalError = response.error;
      }
      setErrors(data);
    }
    else {
      if (response.error) {
        data.generalError = response.error;
      }
      setErrors(data);
    }

    // Scroll to the first error group.
    const elements = document.querySelectorAll(".form-group--error");
    if (elements[0] instanceof HTMLElement) {
      elements[0].scrollIntoView();

      // Focus first element
      const inputElement = elements[0].querySelector("input, select, textarea");

      if (inputElement instanceof HTMLElement) {
        inputElement.focus();
      }
    }
  };

  const renderStep = type => {
    let offset;

    switch(type) {
      case 'next':
        offset = 1;
        break;
      case 'prev':
        offset = -1;
        break;
      default:
        offset = parseInt(type, 10);
    }

    const newStep = currentStep + offset;
    const currentFormSchema = getFormSchema(formName);

    if (!currentFormSchema[newStep]) {
      Logger.error(`Trying to render invalid step "${newStep}".`, "contexts/formContext");
      return;
    }

    // Scroll to the first form rendered by React.
    const elements = document.querySelectorAll("#root form");
    if (elements[0]) {
      elements[0].scrollIntoView();
    }
    setCurrentStep(newStep);
    if (!formData.hasOwnProperty(newStep)) {
      setFormData(prev => {
        prev[newStep] = {};
        return {
          ...prev
        }
      })
    }
  };

  const showToast = () => {
    setToast(true);
  };

  const hideToast = () => {
    setToast(false);
    setToastMessage('');
    setToastLinks([]);
  };

  const triggerRerender = () => {
    setTimesRendered(Math.random());
  };

  return (
    <FormContext.Provider value={{
      getFormSchema,
      setFormSchema,
      schema,
      additionalData,
      setAdditionalData,
      formData,
      setFormData,
      errors,
      setErrors,
      handleErrors,
      handleChange,
      currentStep,
      setCurrentStep,
      renderStep,
      formName,
      setFormName,
      showToast,
      hideToast,
      toastMessage,
      setToastMessage,
      modalOpen,
      setModalOpen,
      toastLinks,
      setToastLinks,
      isAuthenticated,
      timesRendered,
      triggerRerender,
      editedItemKey,
      setEditedItemKey,
      translations,
      setTranslations,
      loading,
      setLoading,
      buttonLoading,
      setButtonLoading
    }}>
      {children}
      <Toast
        show={toast}
        onClose={hideToast}
        message={toastMessage}
        links={toastLinks}
      />
    </FormContext.Provider>
  );
};

export default FormContext;
